import { precisionWord, type PlaceSuggestion, type PlaceSuggestionsDto, type SitePlace } from '@nsb/core';
import { useEffect, useId, useRef, useState } from 'react';
import { api, errorText } from '../api.ts';
import { placeLine } from './placeLine.ts';

/** A typing session's token for Google (a version 4 UUID, as Google recommends): one search, from its first letter to its pick. */
function newSession(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * The site, picked or typed: suggestions as you type, from a street address
 * down to a town or a state. Picking one keeps the place behind the words
 * (research and banners use it); typing past it keeps only the words. While a
 * search is out it says so, and when nothing matched it says that too (P-11,
 * Jason, 2026-10-08: "there should be a searching indicator").
 *
 * The suggestions are Google's once its key is connected (2026-10-27, P-28):
 * each is its words until it is picked, when its place is asked for in the
 * same typing session, so Google bills the pick alone. Photon's are places
 * already. The key stays on the server.
 */
export function PlaceInput({
  id,
  value,
  place,
  onChange,
}: {
  id?: string;
  value: string;
  place: SitePlace | null;
  onChange: (text: string, place: SitePlace | null) => void;
}) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [results, setResults] = useState<PlaceSuggestion[]>([]);
  const [unavailable, setUnavailable] = useState(false);
  const [picking, setPicking] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  // The words the results shown are for: a search is out while they are not the words typed.
  const [answered, setAnswered] = useState<string | null>(null);
  // This search's session: begun by its first question, ended by its pick.
  const session = useRef<string | null>(null);
  const query = value.trim();
  const searching = !place && !picking && query.length >= 3;

  useEffect(() => {
    if (!searching) return;
    let live = true;
    const t = setTimeout(() => {
      session.current ??= newSession();
      api<PlaceSuggestionsDto>(`/places/suggest?q=${encodeURIComponent(query)}&session=${session.current}`)
        .then((r) => {
          if (!live) return;
          setResults(r.suggestions);
          setUnavailable(Boolean(r.unavailable));
          setActive(-1);
          setAnswered(query);
        })
        .catch(() => {
          if (!live) return;
          setResults([]);
          setAnswered(query);
        });
    }, 300);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [query, searching]);

  const shown = searching ? results : [];
  const pick = (s: PlaceSuggestion) => {
    setOpen(false);
    setFailed(null);
    if (s.place) {
      session.current = null;
      onChange(s.place.label, s.place);
      return;
    }
    if (!s.placeId) return;
    const token = session.current;
    // Asking for the place ends the session, whatever Google answers: the next search starts a new one.
    session.current = null;
    setPicking(true);
    api<{ place: SitePlace }>(`/places/pick?id=${encodeURIComponent(s.placeId)}${token ? `&session=${token}` : ''}`)
      .then((r) => onChange(r.place.label, r.place))
      .catch((e) => {
        // Kept as the suggestion's words, with no place behind them, and why.
        setFailed(errorText(e));
        onChange(s.label, null);
      })
      .finally(() => setPicking(false));
  };
  const showList = open && shown.length > 0;

  return (
    <div className="relative">
      <input
        id={id}
        className="field"
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={showList && active >= 0 ? `${listId}-${active}` : undefined}
        aria-busy={picking}
        autoComplete="off"
        placeholder="Address, town or state"
        value={value}
        onChange={(e) => {
          setFailed(null);
          onChange(e.target.value, null);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') return setOpen(false);
          if (!shown.length) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
            setActive((a) => (a + 1) % shown.length);
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((a) => (a <= 0 ? shown.length - 1 : a - 1));
          } else if (e.key === 'Enter' && showList && active >= 0) {
            e.preventDefault();
            pick(shown[active]!);
          }
        }}
      />
      {showList ? (
        <ul
          id={listId}
          role="listbox"
          className="absolute inset-x-0 top-full z-20 mt-1 divide-y divide-rule rounded-[4px] border border-rule bg-sheet shadow-[0_12px_32px_rgba(20,22,19,.10)]"
        >
          {shown.map((p, i) => (
            <li
              key={p.placeId ?? p.label}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              // mousedown, not click: it lands before the input's blur closes the list
              onMouseDown={(e) => {
                e.preventDefault();
                pick(p);
              }}
              className={`flex min-h-11 cursor-pointer items-baseline justify-between gap-4 px-3 py-2.5 ${i === active ? 'bg-inset' : 'hover:bg-inset'}`}
            >
              <span>{p.label}</span>
              {p.precision ? <span className="label shrink-0">{precisionWord(p.precision)}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
      <p className="meta mt-1 min-h-[1.25rem]" aria-live="polite">
        {placeLine({ place, unavailable, searching, pending: answered !== query, found: answered === query ? results.length : null, picking, failed })}
      </p>
    </div>
  );
}
